import { collaboratorRole, collaboratorStatus, LOGISTICS_VEHICLE_TYPES } from '@gozoki/api-types'
import { LOGISTICS_SEARCH_MAX_LENGTH } from '@gozoki/api-types/src/validation/logistics'
import { z } from 'zod'
import {
  SITE_KEY_PATTERN,
  VEHICLE_ATTACHMENT_KEY_PATTERN,
} from '~/components/routes/logistics/logistics-places'
import { realIsoDateSchema } from '~/lib/iso-date'

/** Filtre « Camions basés » de la liste des hubs : aucun, ou au moins un. */
const HUB_VEHICLES_FILTERS = ['none', 'some'] as const

/**
 * Filtre « Surface » de la liste des hubs, par tranche : bornes basses incluses, hautes exclues
 * (`500-1000` = de 500 à moins de 1 000 m²). `unknown` isole les hubs sans surface renseignée.
 */
const HUB_SURFACE_FILTERS = ['lt500', '500-1000', '1000-2000', 'gte2000', 'unknown'] as const

const HUB_STATUS_FILTERS = ['active', 'inactive'] as const

export const LOGISTICS_TAB_VALUES = ['simulator', 'operations', 'hubs', 'vehicles', 'hr'] as const
export type LogisticsTabValue = (typeof LOGISTICS_TAB_VALUES)[number]

/**
 * Filtres globaux du simulateur, portés par l'URL : ils survivent au rafraîchissement et au
 * partage de lien. `kitchen`/`department` sélectionnent au plus une valeur (pas de multi-sélection
 * ici) ; `from`/`to` gardent les tournées actives pendant la période inclusive ; `q` est la
 * recherche libre du panneau gauche, débouncée côté composant avant d'atterrir ici.
 */
export const logisticsSearchSchema = z
  .object({
    tab: z.enum(LOGISTICS_TAB_VALUES).catch('simulator'),
    kitchen: z.coerce.number().int().positive().optional().catch(undefined),
    // Mêmes bornes que l'API (`GET /logistics/simulations`) : une valeur hors bornes est ignorée
    // ici plutôt que de faire refuser toute la liste en 422.
    department: z.string().trim().toUpperCase().min(2).max(3).optional().catch(undefined),
    from: realIsoDateSchema.optional().catch(undefined),
    to: realIsoDateSchema.optional().catch(undefined),
    // TanStack conserve les clés inconnues : effacer explicitement l'ancien filtre des liens.
    day: z.undefined().catch(undefined),
    // Recherche vide une fois rognée (espaces seuls) : ignorée, comme côté API.
    q: z.string().trim().min(1).max(LOGISTICS_SEARCH_MAX_LENGTH).optional().catch(undefined),
    // Onglet Camions : recherche et filtres de la liste, conservés à l'aller-retour vers une fiche.
    // Filtrage local (la liste est lue en entier) : une valeur mal formée est simplement ignorée.
    vehicleQ: z.string().trim().min(1).max(LOGISTICS_SEARCH_MAX_LENGTH).optional().catch(undefined),
    vehicleType: z.enum(LOGISTICS_VEHICLE_TYPES).optional().catch(undefined),
    vehicleAttachment: z.string().regex(VEHICLE_ATTACHMENT_KEY_PATTERN).optional().catch(undefined),
    vehicleSite: z.string().regex(SITE_KEY_PATTERN).optional().catch(undefined),
    vehicleAvailability: z.enum(['available', 'unavailable']).optional().catch(undefined),
    // Onglet Hubs : même principe que l'onglet Camions (filtrage local, valeur mal formée ignorée).
    hubQ: z.string().trim().min(1).max(LOGISTICS_SEARCH_MAX_LENGTH).optional().catch(undefined),
    hubKitchen: z.coerce.number().int().positive().optional().catch(undefined),
    hubVehicles: z.enum(HUB_VEHICLES_FILTERS).optional().catch(undefined),
    hubSurface: z.enum(HUB_SURFACE_FILTERS).optional().catch(undefined),
    hubStatus: z.enum(HUB_STATUS_FILTERS).optional().catch(undefined),
    // Onglet RH : filtres comme l'onglet Camions. Sa recherche, elle, reste hors de l'URL : elle
    // porte sur des données personnelles (nom, mail, téléphone) qui n'ont rien à faire dans
    // l'historique, un lien partagé ou le fil de navigation joint aux erreurs Sentry.
    hrRole: z.enum(collaboratorRole).optional().catch(undefined),
    hrStatus: z.enum(collaboratorStatus).optional().catch(undefined),
  })
  .transform((search) => {
    if (search.from === undefined && search.to === undefined) return search
    if (search.from !== undefined && search.to !== undefined && search.from <= search.to)
      return search
    // L'API exige une paire complète et ordonnée : ignorer toute la période d'un lien mal formé.
    return { ...search, from: undefined, to: undefined }
  })

export type LogisticsSearch = z.infer<typeof logisticsSearchSchema>
